import type {
  V1CodeInput,
  V1CodeOutput,
  V1CodeManifest,
} from '@/generated/hooks/grimoire/models';
import { formatValueType } from '@/modules/automation/components/AutomationEditor/manifest-value-type';
import { groupStepsByPhase } from '@/modules/automation/utils/group-steps-by-phase';

/** Section headings for the exported document. Passed in from the caller's
 *  `useTranslation` rather than read here, so this stays a pure function. */
export interface IManifestExportLabels {
  inputs: string;
  outputs: string;
  steps: string;
  untitledStep: string;
  name: string;
  type: string;
  description: string;
}

/** Escapes the characters that would otherwise break out of a GFM table cell.
 *
 *  Backslashes go first, and that order is the whole point: escaping `|` into
 *  `\|` while leaving backslashes alone turns an input of `a\|b` into `a\\|b`,
 *  which renders as a literal backslash followed by a live cell break. */
function escapeTableCell(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/\|/g, '\\|')
    .replace(/\s*\n\s*/g, ' ')
    .trim();
}

/** One GFM table of a manifest's inputs or outputs: a header row, the alignment
 *  row, then a row per item. Returned as lines for the caller to join. */
function ioTable(
  items: (V1CodeInput | V1CodeOutput)[],
  labels: IManifestExportLabels,
): string[] {
  return [
    `| ${labels.name} | ${labels.type} | ${labels.description} |`,
    '| --- | --- | --- |',
    ...items.map(item => {
      const cells = [
        item.name ?? '',
        formatValueType(item.type),
        item.description ?? '',
      ].map(escapeTableCell);
      return `| ${cells.join(' | ')} |`;
    }),
  ];
}

/**
 * Serialises a built automation's `CodeManifest` to a standalone Markdown
 * SOP document — the same content the step view renders (title, overview,
 * inputs, phase-grouped steps with their rules, outputs), in the same order.
 *
 * The output is plain GFM with no HTML, so it is also the input the DOCX and
 * PDF exports are rendered from: one source of truth for what "the SOP"
 * contains, rather than three format-specific walks of the manifest.
 */
export function manifestToMarkdown(
  manifest: V1CodeManifest,
  labels: IManifestExportLabels,
  titleFallback?: string,
): string {
  const lines: string[] = [];
  const inputs = manifest.inputs ?? [];
  const outputs = manifest.outputs ?? [];

  lines.push(`# ${manifest.title || titleFallback || ''}`.trimEnd());
  if (manifest.overview) lines.push('', manifest.overview);

  if (inputs.length > 0) {
    lines.push('', `## ${labels.inputs}`, '', ...ioTable(inputs, labels));
  }

  const phaseGroups = groupStepsByPhase(manifest.steps);
  if (phaseGroups.length > 0) {
    lines.push('', `## ${labels.steps}`);
    for (const group of phaseGroups) {
      if (group.phase) lines.push('', `### ${group.phase}`);
      for (const step of group.steps) {
        const heading = [step.number, step.title || labels.untitledStep]
          .filter(part => part !== undefined && part !== '')
          .join('. ');
        lines.push('', `#### ${heading}`);
        if (step.overview) lines.push('', step.overview);
        // Rules render as a blockquote, matching how the step view presents
        // them (a quieter callout beneath the overview).
        for (const rule of step.rules ?? []) {
          lines.push('', `> ${rule.replace(/\n/g, '\n> ')}`);
        }
      }
    }
  }

  if (outputs.length > 0) {
    lines.push('', `## ${labels.outputs}`, '', ...ioTable(outputs, labels));
  }

  return `${lines.join('\n')}\n`;
}
